import Phaser from "phaser";
import type UIPlugin from "phaser3-rex-plugins/templates/ui/ui-plugin.js";
import type RoundRectangle from "phaser3-rex-plugins/plugins/roundrectangle.js";
import { THEME, TEXT_STYLE } from "./theme";

interface RexScene extends Phaser.Scene {
  rexUI: UIPlugin;
}

interface HpBarConfig {
  scene: Phaser.Scene;
  x: number;
  y: number;
  width: number;
  label: string;
  hp: number;
  maxHp: number;
  color: number;
}

export class HpBar {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private trackFill: RoundRectangle;
  private valueText: Phaser.GameObjects.Text;
  private trackWidth: number;
  private maxHp: number;

  constructor(cfg: HpBarConfig) {
    const rex = (cfg.scene as RexScene).rexUI;
    this.scene = cfg.scene;
    this.maxHp = cfg.maxHp;

    const padX = 12;
    const padY = 6;
    const labelW = 36;
    const valueW = 90;
    const innerH = 18;
    this.trackWidth = cfg.width - padX * 2 - labelW - valueW - 16;
    const barHeight = innerH + padY * 2;

    const panel = rex.add.roundRectangle(0, 0, cfg.width, barHeight, 8, THEME.bgPanel)
      .setStrokeStyle(1, THEME.borderAccent, 0.6);

    const labelText = cfg.scene.add.text(0, 0, cfg.label, {
      ...TEXT_STYLE.small,
      fontSize: "11px",
      fontStyle: "bold",
    }).setOrigin(0.5);
    labelText.setLetterSpacing(1.5);

    const trackBgX = -cfg.width / 2 + padX + labelW + 8;
    const trackBg = rex.add.roundRectangle(0, 0, this.trackWidth, innerH, 5, THEME.bgDeep)
      .setStrokeStyle(1, THEME.borderSubtle, 0.8)
      .setOrigin(0, 0.5);
    trackBg.x = trackBgX;

    this.trackFill = rex.add.roundRectangle(0, 0, this.trackWidth, innerH - 4, 4, cfg.color)
      .setOrigin(0, 0.5);
    this.trackFill.x = trackBgX + 2;

    this.valueText = cfg.scene.add.text(0, 0, `${cfg.hp} / ${cfg.maxHp}`, {
      ...TEXT_STYLE.body,
      fontSize: "13px",
      fontStyle: "bold",
      color: "#ffffff",
    }).setOrigin(0, 0.5);
    this.valueText.x = trackBgX + this.trackWidth + 12;

    labelText.x = -cfg.width / 2 + padX + labelW / 2;
    labelText.y = 0;

    this.container = cfg.scene.add.container(cfg.x, cfg.y, [
      panel, labelText, trackBg, this.trackFill, this.valueText,
    ]);
    this.container.setSize(cfg.width, barHeight);

    this.applyHp(cfg.hp, false);
  }

  setHp(hp: number, maxHp?: number) {
    if (maxHp !== undefined) this.maxHp = maxHp;
    this.applyHp(hp, true);
  }

  private applyHp(hp: number, animate: boolean) {
    const safe = Math.max(0, Math.min(hp, this.maxHp));
    const pct = this.maxHp <= 0 ? 0 : safe / this.maxHp;
    const targetW = Math.max(0, this.trackWidth - 4) * pct;

    if (animate) {
      this.scene.tweens.add({
        targets: this.trackFill,
        width: targetW,
        duration: 350,
        ease: "Sine.easeOut",
      });
    } else {
      this.trackFill.width = targetW;
    }
    this.valueText.setText(`${safe} / ${this.maxHp}`);
  }

  destroy() {
    this.container.destroy(true);
  }
}
